import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useEffect } from 'react';
import { handleSessionError } from '@/lib/sessionErrorHandler';

export interface PortalMessage {
  id: string;
  company_id: string;
  customer_id: string;
  project_id: string | null;
  sender_type: 'staff' | 'customer';
  sender_name: string | null;
  message: string;
  is_read: boolean;
  created_at: string;
  message_type?: 'text' | 'email_sent' | 'email_received' | 'sms_sent' | 'sms_received' | string;
  subject?: string | null;
  metadata?: Record<string, any> | null;
}

export function useOfficePortalMessages(projectId: string | undefined, customerId: string | undefined) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['portal-messages', projectId, customerId],
    staleTime: 30 * 1000,
    queryFn: async () => {
      // Find all sibling customer IDs sharing the same email (handles duplicate customer records)
      let allCustomerIds: string[] = customerId ? [customerId] : [];
      if (customerId) {
        const { data: customerData } = await supabase
          .from('customers')
          .select('email')
          .eq('id', customerId)
          .maybeSingle();
        if (customerData?.email) {
          // Case-insensitive match: customers can have same email with different casings
          // (e.g. "Elyaslalusi@gmail.com" vs "elyaslalusi@gmail.com") and we want all
          // duplicate-customer messages to surface in the same thread.
          const { data: siblings } = await supabase
            .from('customers')
            .select('id')
            .ilike('email', customerData.email);
          if (siblings && siblings.length > 0) {
            allCustomerIds = [...new Set(siblings.map(s => s.id))];
          }
        }
      }

      let q = supabase
        .from('portal_messages')
        .select('*')
        .order('created_at', { ascending: true });

      // Show messages linked to this project OR from any sibling customer
      if (projectId && allCustomerIds.length > 0) {
        const cidList = allCustomerIds.map(cid => `customer_id.eq.${cid}`).join(',');
        q = q.or(`project_id.eq.${projectId},${cidList}`);
      } else if (projectId) {
        q = q.eq('project_id', projectId);
      } else if (allCustomerIds.length > 0) {
        q = q.in('customer_id', allCustomerIds);
      }

      const { data, error } = await q;
      if (error) throw error;
      return (data || []) as PortalMessage[];
    },
    enabled: !!(projectId || customerId),
  });

  // Realtime subscription
  useEffect(() => {
    if (!customerId && !projectId) return;
    const channel = supabase
      .channel(`portal-msgs-${projectId || customerId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'portal_messages',
      }, (payload) => {
        const row = payload.new as any;
        if (!row) return;
        // Invalidate when the insert touches the open project or any related customer thread
        if (
          (projectId && row.project_id === projectId) ||
          (customerId && row.customer_id === customerId)
        ) {
          queryClient.invalidateQueries({ queryKey: ['portal-messages', projectId, customerId] });
        }
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [projectId, customerId, queryClient]);

  return query;
}

export function useOfficeSendPortalMessage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ projectId, customerId, message }: {
      projectId?: string;
      customerId: string;
      message: string;
    }) => {
      if (!user?.company_id) throw new Error('Ingen virksomhed fundet');

      const { error } = await supabase.from('portal_messages').insert({
        company_id: user.company_id,
        customer_id: customerId,
        project_id: projectId || null,
        sender_type: 'staff',
        sender_name: user.full_name || user.email || 'Kontor',
        message,
        is_read: false,
        message_type: 'text',
      } as any);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-messages'] });
      queryClient.invalidateQueries({ queryKey: ['inbox-portal-conversations'] });
      queryClient.invalidateQueries({ queryKey: ['inbox-messages'] });
    },
  });
}

export function useOfficeSendPortalEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, customerId, message, subject, signature_html, attachments }: {
      projectId?: string;
      customerId: string;
      message: string;
      subject?: string;
      signature_html?: string;
      attachments?: Array<{ storage_path: string; bucket: string; filename: string }>;
    }) => {
      const { data, error } = await supabase.functions.invoke('send-portal-email', {
        body: { project_id: projectId, customer_id: customerId, message, subject, signature_html, attachments },
      });

      if (error) {
        handleSessionError(error);
        let detail = '';
        try {
          const body = error.context?.body ? JSON.parse(typeof error.context.body === 'string' ? error.context.body : await error.context.body?.text?.() || '{}') : null;
          detail = body?.error || '';
        } catch { /* ignore */ }
        throw new Error(detail || error.message || 'Email afsendelse fejlede');
      }
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-messages'] });
      queryClient.invalidateQueries({ queryKey: ['inbox-portal-conversations'] });
      queryClient.invalidateQueries({ queryKey: ['inbox-messages'] });
    },
  });
}

export function useOfficeSendSms() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ projectId, customerId, message, recipientPhone }: {
      projectId?: string;
      customerId: string;
      message: string;
      recipientPhone: string;
    }) => {
      const res = await supabase.functions.invoke('send-sms', {
        body: {
          recipient: recipientPhone,
          message,
          project_id: projectId,
          customer_id: customerId,
          sender_name: user?.full_name || user?.email || 'Kontor',
        },
      });

      const extractErr = async (err: any): Promise<string> => {
        try {
          const ctx = err?.context;
          if (ctx instanceof Response) {
            const txt = await ctx.clone().text();
            try { const j = JSON.parse(txt); return j?.error || j?.message || txt; } catch { return txt; }
          }
          if (ctx?.body) {
            const txt = typeof ctx.body === 'string'
              ? ctx.body
              : (ctx.body instanceof ReadableStream
                  ? await new Response(ctx.body).text()
                  : (await (ctx.body as any)?.text?.()) || '');
            try { const j = JSON.parse(txt); return j?.error || j?.message || txt; } catch { return txt; }
          }
        } catch { /* ignore */ }
        return err?.message || '';
      };

      if (res.error) {
        handleSessionError(res.error);
        const detail = await extractErr(res.error);
        console.error('[send-sms] invoke error:', res.error, 'detail:', detail);
        throw new Error(detail || 'SMS afsendelse fejlede');
      }
      if (res.data && res.data.success === false) {
        console.error('[send-sms] backend failure:', res.data);
        throw new Error(res.data.error || 'SMS afsendelse fejlede');
      }
      if (res.data?.error) throw new Error(res.data.error);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-messages'] });
      queryClient.invalidateQueries({ queryKey: ['inbox-portal-conversations'] });
      queryClient.invalidateQueries({ queryKey: ['inbox-messages'] });
    },
  });
}
